import { Link } from 'react-router-dom';
import { Typography } from 'antd';
import PropTypes from 'prop-types';
import { NAV_DONATE } from '../../constants';

const { Paragraph } = Typography;

/**
 * A quiet, inline support ask placed at the point where a reader has just
 * received something from the archive: the end of an account, or the end of
 * the statistics page.
 *
 * The /donate page itself gets very little traffic, because the only routes to
 * it are the nav bar and the footer. Nobody navigates to a donate page out of
 * curiosity; they give at the moment the thing in front of them turned out to
 * be worth something. This component exists to be present at that moment.
 *
 * Deliberately understated. People read these accounts while grieving, or after
 * their own near-death experience. An aggressive ask in that context would be
 * tasteless, and would cost more goodwill than it raised. No modal, no banner,
 * no colour block. Body text, a rule above it, and one link.
 */
const SupportPrompt = ({ variant = 'nde', style }) => {
  const copy = {
    // After reading a single account: the ask is about preservation, since that
    // is what the reader has just personally benefited from.
    nde: (
      <>
        This account is free to read, with no ads and no paywall, and stays that way because
        a handful of readers cover the cost. Hosting the archive runs about €12 a month.{' '}
        <Link to={NAV_DONATE}>You can help keep it online</Link>.
      </>
    ),
    // After the statistics page: the reader has just used the analysis, so the
    // ask is about the work behind it rather than about storage.
    stats: (
      <>
        Every figure on this page comes from thousands of first-hand accounts, collected and
        structured one at a time. The archive is free, ad-free, and costs about €12 a month
        to run. <Link to={NAV_DONATE}>You can help keep it that way</Link>.
      </>
    ),
    // After a blog post: whoever reached the end of one already knows what the
    // project is, so this variant does not re-explain it.
    blog: (
      <>
        NDE Archive is a one-person project, free to use and funded out of pocket at about
        €12 a month. <Link to={NAV_DONATE}>You can chip in here</Link> if you want to keep it
        going.
      </>
    ),
    // On /about the reader has just read why the project exists, so this
    // variant is the only one that asks outright.
    about: (
      <>
        There is no funding behind any of this and no ads on the site. Hosting runs about
        €12 a month, out of pocket.{' '}
        <Link to={NAV_DONATE}>If it is worth something to you, you can help cover it</Link>.
      </>
    )
  };

  return (
    <div
      style={{
        maxWidth: 680,
        margin: '48px auto 8px',
        paddingTop: 24,
        borderTop: '1px solid rgba(145, 149, 252, 0.25)',
        ...style
      }}
    >
      <Paragraph
        style={{
          fontSize: 14,
          lineHeight: '24px',
          color: '#777',
          marginBottom: 0,
          textAlign: 'center'
        }}
      >
        {copy[variant] || copy.nde}
      </Paragraph>
    </div>
  );
};

SupportPrompt.propTypes = {
  variant: PropTypes.oneOf(['nde', 'stats', 'blog', 'about']),
  style: PropTypes.object
};

export default SupportPrompt;
